import { EntryFieldTypes, EntrySkeletonType, Entry } from 'contentful';

//---------------------------------------------------------
// Content Model

export type JsonObject = { [Key in string]: string };

//The list of all types that are allowed inside a "Content Container" (see cmsContentSection)
export type ContentContainerCompatibleContentType =
  | RichTextContentType
  | LinkContentType
  | TextContentType
  | ImageContentType
  | ColumnsContentType
  | ConditionalContainerContentType
  | TabsContentType
  | TimerContentType
  | never;

export type WithoutUnResolveable<T extends EntrySkeletonType> = Entry<T, 'WITHOUT_UNRESOLVABLE_LINKS'>;
export type ArrayFieldType<T extends EntrySkeletonType> = EntryFieldTypes.Array<EntryFieldTypes.EntryLink<T>>;
export type ArrayEntryType<T> = (T | undefined)[];
//RichText Content-Type
export type RichTextFields = {
  entryTitle: EntryFieldTypes.Text;
  content?: EntryFieldTypes.Text;
  contentNew?: EntryFieldTypes.Text;
  backgroundColour?: EntryFieldTypes.Text;
};
export type RichTextContentType = EntrySkeletonType<RichTextFields, 'richtext'>;
export type RichTextEntryType = WithoutUnResolveable<RichTextContentType>;

export type ArticleFields = {
  entryTitle: EntryFieldTypes.Symbol;
  link: EntryFieldTypes.Symbol;
  media: EntryFieldTypes.EntryLink<ImageContentType>;
  primaryHeader: EntryFieldTypes.Symbol;
  secondaryHeader?: EntryFieldTypes.Symbol;
  content: EntryFieldTypes.EntryLink<RichTextContentType>;
  publishedDate?: EntryFieldTypes.Date;
};

export type ArticleContentType = EntrySkeletonType<ArticleFields, 'article'>;
export type ArticleEntryType = WithoutUnResolveable<ArticleContentType>;

export interface ColourField extends JsonObject {
  //See Contentful custom App 'Color Picker'
  id: string;
  name: string;
  value: string;
}
export interface IconFields {
  entryTitle: EntryFieldTypes.Text;
  iconType: EntryFieldTypes.Text;
  colour?: EntryFieldTypes.Object<ColourField>;
  altText?: EntryFieldTypes.Text;
}
export type IconContentType = EntrySkeletonType<IconFields, 'icon'>;
export type IconEntryType = WithoutUnResolveable<IconContentType>;

//Link Content-Type
export interface LinkFields {
  entryTitle: EntryFieldTypes.Text;
  title: EntryFieldTypes.Text;
  url?: EntryFieldTypes.Text;
  icon?: EntryFieldTypes.EntryLink<IconContentType | ImageContentType>;
  targeting?: EntryFieldTypes.Text;
  variant?: EntryFieldTypes.Text;
  productCode?: EntryFieldTypes.Text;
  fullWidth?: EntryFieldTypes.Boolean;
  openInNewTab?: EntryFieldTypes.Boolean;
  enableMobileStickyAddToBasket?: EntryFieldTypes.Boolean;
}
export type LinkContentType = EntrySkeletonType<LinkFields, 'link'>;
export type LinkEntryType = WithoutUnResolveable<LinkContentType>;

//Text Content-Type
export type TextVariant = 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'seperator' | 'blank';
export interface TextFields {
  title: EntryFieldTypes.Text;
  variant: EntryFieldTypes.Text<TextVariant>;
  padding?: EntryFieldTypes.Number;
}
export type TextContentType = EntrySkeletonType<TextFields, 'text'>;
export type TextEntryType = WithoutUnResolveable<TextContentType>;

//Widget Content-Type
export type WidgetVariant =
  | 'separator'
  | 'blank'
  | 'Customer Review Full Width Carousel'
  | 'Recently Viewed'
  | 'Trending Items'
  | 'Related Items';
export interface WidgetFields {
  title: EntryFieldTypes.Text;
  variant: EntryFieldTypes.Text<WidgetVariant>;
  padding?: EntryFieldTypes.Number;
  tags?: EntryFieldTypes.Text;
  productCode?: EntryFieldTypes.Text;
  wineType?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  grape?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  country?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  productType?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  awardWinner?: EntryFieldTypes.Boolean;
  giftItems?: EntryFieldTypes.Boolean;
  offerItems?: EntryFieldTypes.Boolean;
  fineWine?: EntryFieldTypes.Boolean;
  widgetTitle?: EntryFieldTypes.Text;
  maxTrendingProducts?: EntryFieldTypes.Integer;
}
export type WidgetContentType = EntrySkeletonType<WidgetFields, 'widget'>;
export type WidgetEntryType = WithoutUnResolveable<WidgetContentType>;

//Image Content-Type
export interface ImageFields {
  title: EntryFieldTypes.Text;
  altText?: EntryFieldTypes.Text;
  largeDeviceImage: EntryFieldTypes.AssetLink;
  smallDeviceImage?: EntryFieldTypes.AssetLink;
  url?: EntryFieldTypes.Text;
  entryTitle?: EntryFieldTypes.Text;
}
export type ImageContentType = EntrySkeletonType<ImageFields, 'image'>;
export type ImageEntryType = WithoutUnResolveable<ImageContentType>;

//video content type
export interface VideoFields {
  title: EntryFieldTypes.Text;
  varient: EntryFieldTypes.Text;
  videoLink: EntryFieldTypes.Text;
  smallDeviceVideoLink: EntryFieldTypes.Text;
  image?: EntryFieldTypes.EntryLink<ImageContentType>;
  videoText: EntryFieldTypes.Text;
  buttonText: EntryFieldTypes.Text;
  alignment: EntryFieldTypes.Text;
}
export type VideoContentType = EntrySkeletonType<VideoFields, 'video'>;
export type VideoEntryType = WithoutUnResolveable<VideoContentType>;

//Columns Content-Type
export interface ColumnsFields {
  entryTitle: EntryFieldTypes.Text;
  content: ArrayFieldType<ColumnContentType>;
}
export type ColumnsContentType = EntrySkeletonType<ColumnsFields, 'columns'>;
export type ColumnsEntryType = WithoutUnResolveable<ColumnsContentType>;

//Column Content-Type
export interface ColumnFields {
  entryTitle: EntryFieldTypes.Text;
  width: EntryFieldTypes.Integer;
  desktopWidth: EntryFieldTypes.Integer;
  content: ArrayFieldType<ContentContainerCompatibleContentType>;
}

export type ColumnContentType = EntrySkeletonType<ColumnFields, 'column'>;
export type ColumnEntryType = WithoutUnResolveable<ColumnContentType>;

//Tabs Content-Type
export interface TabsFields {
  entryTitle: EntryFieldTypes.Text;
  tabPanes: ArrayFieldType<TabContentType>;
}
export type TabsContentType = EntrySkeletonType<TabsFields, 'tabs'>;
export type TabsEntryType = WithoutUnResolveable<TabsContentType>;

//Tab Content-Type
export interface TabFields {
  title: EntryFieldTypes.Text;
  content: ArrayFieldType<ContentContainerCompatibleContentType>;
  tabUrl: EntryFieldTypes.Text;
  emphasizeTitle: EntryFieldTypes.Boolean;
  stickyContent?: ArrayFieldType<LinkContentType>;
}
export type TabContentType = EntrySkeletonType<TabFields, 'tab'>;
export type TabEntryType = WithoutUnResolveable<TabContentType>;

//Container Content-Type
export interface ContainerFields {
  title: EntryFieldTypes.Text;
  content: ArrayFieldType<ContentContainerCompatibleContentType>;
  backgroundColour?: EntryFieldTypes.Text;
  alignment?: EntryFieldTypes.Text;
  pageWidthToggle?: EntryFieldTypes.Boolean;
  fluidWidth?: EntryFieldTypes.Boolean;
}
export type ContainerContentType = EntrySkeletonType<ContainerFields, 'container'>;
export type ContainerEntryType = WithoutUnResolveable<ContainerContentType>;

//HeaderNavigation Content-Type
export type HeaderNavigationFields = {
  navigations: ArrayFieldType<LinkContentType>;
};
export type HeaderNavigationContentType = EntrySkeletonType<HeaderNavigationFields, 'headerNavigation'>;
export type HeaderNavigationEntryType = WithoutUnResolveable<HeaderNavigationContentType>;

//Navigation Content-Type
export interface NavigationFields {
  entryTitle: EntryFieldTypes.Text;
  title: EntryFieldTypes.Text;
  content?: ArrayFieldType<LinkContentType>;
  sectionLink?: EntryFieldTypes.EntryLink<LinkContentType>;
  openAccordionByDefault?: EntryFieldTypes.Boolean;
}
export type NavigationContentType = EntrySkeletonType<NavigationFields, 'navigation'>;
export type NavigationEntryType = WithoutUnResolveable<NavigationContentType>;

// GuideLinePolicy Content-Type
export interface GuideLinePolicyFields {
  entryTitle: EntryFieldTypes.Text;
  title: EntryFieldTypes.Text;
  description?: EntryFieldTypes.Text;
  image?: EntryFieldTypes.AssetLink;
}
export type GuideLinePolicyContentType = EntrySkeletonType<GuideLinePolicyFields, 'guidelinePolicyConfig'>;
export type GuideLinePolicyEntryType = WithoutUnResolveable<GuideLinePolicyContentType>;

//Banner Content-Type
export interface BannerFields {
  entryTitle: EntryFieldTypes.Text;
  variant: EntryFieldTypes.Text;
  backgroundImage: EntryFieldTypes.EntryLink<ImageContentType>;
  largeDeviceVimeoVideo: EntryFieldTypes.Text;
  smallDeviceVimeoVideo: EntryFieldTypes.Text;
  bannerContent: EntryFieldTypes.Text;
  image: EntryFieldTypes.EntryLink<ImageContentType>;
  bannerLink?: EntryFieldTypes.Text;
  contentBackgroundColour: EntryFieldTypes.Text;
  contentWidth: EntryFieldTypes.Text;
  minimumBannerHeight?: EntryFieldTypes.Number;
  alignment: EntryFieldTypes.Text;
  mainCtaLink?: EntryFieldTypes.EntryLink<LinkContentType>;
  secondaryTextLink?: EntryFieldTypes.EntryLink<LinkContentType>;
  altText?: EntryFieldTypes.Text;
  CTALink: ArrayFieldType<RichTextContentType>;
  addGradient?: EntryFieldTypes.Boolean;
  hideImageOnMobile?: EntryFieldTypes.Boolean;
}
export type BannerContentType = EntrySkeletonType<BannerFields, 'banner'>;
export type BannerEntryType = WithoutUnResolveable<BannerContentType>;

//react component
export interface BannerProps {
  contentItem: BannerEntryType;
  dlClickEventHandler?: (data: string) => void;
}

//Product Card Component
export interface ProductCardFields {
  entryTitle: EntryFieldTypes.Text;
  productCode: EntryFieldTypes.Text;
  productCardBackgroundImage: EntryFieldTypes.EntryLink<ImageContentType>;
  productCardBackgroundColor: EntryFieldTypes.Object<ColourField>;
  productCardButton: EntryFieldTypes.Text;
  productCardVariant: EntryFieldTypes.Text;
  productCardTitle: EntryFieldTypes.Text;
  productCardTitleUrl: EntryFieldTypes.Text;
  productCardSubTitle: EntryFieldTypes.Text;
  productCardDescription: EntryFieldTypes.EntryLink<RichTextContentType>;
  productImageOverride: EntryFieldTypes.EntryLink<ImageContentType>;
  productImageSecondary: EntryFieldTypes.EntryLink<ImageContentType>;
  productLink?: ArrayFieldType<LinkContentType>;
  productNumberOfBottles: EntryFieldTypes.Integer;
  hidePriceOnMultiView: EntryFieldTypes.Boolean;
}
export type ProductCardType = EntrySkeletonType<ProductCardFields, 'banner'>;
export type ProductCardEntryType = WithoutUnResolveable<ProductCardType>;

export interface CmsProductCardProps {
  contentItem: ProductCardEntryType;
}

//Expander Component
export interface ExpanderFields {
  entryTitle: EntryFieldTypes.Text;
  expanderTitle: EntryFieldTypes.Text;
  closerTitle: EntryFieldTypes.Text;
  content: EntryFieldTypes.EntryResourceLink<ContentContainerCompatibleContentType>;
  backgroundColor: EntryFieldTypes.Text;
  titleColor: EntryFieldTypes.Text;
}
export type ExpanderType = EntrySkeletonType<ExpanderFields, 'expander'>;
export type ExpanderEntryType = WithoutUnResolveable<ExpanderType>;

//Banner Content-Type
export interface AccordionFields {
  reference: ArrayFieldType<RichTextContentType>;
}
export type AccordionContentType = EntrySkeletonType<AccordionFields, 'accordion'>;
export type AccordionEntryType = WithoutUnResolveable<AccordionContentType>;

//HTML Code Content Type
export type HtmlCodeFields = {
  entryTitle: EntryFieldTypes.Text;
  content?: EntryFieldTypes.Text;
};
export type HtmlCodeContentType = EntrySkeletonType<HtmlCodeFields, 'htmlCode'>;
export type HtmlCodeEntryType = WithoutUnResolveable<HtmlCodeContentType>;

//react component
export interface AccordionProps {
  contentItem: AccordionEntryType;
}

export interface MenuBannerFields {
  menuBannerImage: EntryFieldTypes.AssetLink;
  menuBannerUrl: EntryFieldTypes.Text;
}
export type MenuBannerContentType = EntrySkeletonType<MenuBannerFields, 'menuBanner'>;
export type MenuBannerEntryType = WithoutUnResolveable<MenuBannerContentType>;

export interface MenuBannerProps {
  contentItem: MenuBannerEntryType;
  dlClickEventHandler?: (data: string) => void;
}

export interface Flag {
  key: string;
  type: string;
  value: boolean;
}
export interface FlagsState {
  flags: Flag[];
}

//Site Content-Type
export interface SiteFields {
  entryTitle: EntryFieldTypes.Text;
  stickyHeader: EntryFieldTypes.Boolean;
  stripBanner: EntryFieldTypes.Text;
  stripBannerBackground: EntryFieldTypes.Object<ColourField>;
  mainNavbar: ArrayFieldType<TabsContentType>;
  favorites: EntryFieldTypes.EntryLink<LinkContentType>;
  accountNavbar?: EntryFieldTypes.EntryLink<HeaderNavigationContentType>;
  footerNavBar?: ArrayFieldType<NavigationContentType | LinkContentType>;
  socialLinks?: ArrayFieldType<LinkContentType>;
  copyright: EntryFieldTypes.RichText;
  guidelinePolicy?: ArrayFieldType<GuideLinePolicyContentType>;
  badgeImages: ArrayFieldType<LinkContentType>;
  miniCartEnabled?: EntryFieldTypes.Boolean;
  featureFlag?: FlagsState;
}

export type SiteContentType = EntrySkeletonType<SiteFields, 'site'>;
export type SiteEntryType = WithoutUnResolveable<SiteContentType>;

//SEO Content-Type
export interface SeoFields {
  entryTitle: EntryFieldTypes.Text;
  seoTitle: EntryFieldTypes.Text;
  description?: EntryFieldTypes.Text;
  canonicalLink?: EntryFieldTypes.Text;
  h1Title?: EntryFieldTypes.Text;
  robots?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  googleBots?: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  socialSearchImage?: EntryFieldTypes.EntryLink<ImageContentType>;
}
export type SeoContentType = EntrySkeletonType<SeoFields, 'seo'>;
export type SeoEntryType = WithoutUnResolveable<SeoContentType>;

//Sponsored Content Type
export interface SponsoredFields {
  entryTitle: EntryFieldTypes.Text;
  productIds: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  sponsoredLabel?: EntryFieldTypes.Symbol;
}
export type SponsoredContentType = EntrySkeletonType<SponsoredFields, 'sponsored'>;
export type SponsoredEntryType = WithoutUnResolveable<SponsoredContentType>;

//Content Page Content-Type
export interface ContentPageFields {
  entryTitle: EntryFieldTypes.Text;
  id?: EntryFieldTypes.Symbol;
  slug: EntryFieldTypes.Text;
  seoMeta?: EntryFieldTypes.EntryLink<SeoContentType>;
  layout: ArrayFieldType<ContentContainerCompatibleContentType>;
  variant?: EntryFieldTypes.Text;
  bottomSection?: ArrayFieldType<ContentContainerCompatibleContentType>;
  topSection?: ArrayFieldType<ContentContainerCompatibleContentType>;
  sidebar?: ArrayFieldType<ContentContainerCompatibleContentType>;
  topSectionPlp?: ArrayFieldType<ContentContainerCompatibleContentType>;
  bottomSectionPlp?: ArrayFieldType<ContentContainerCompatibleContentType>;
  topSectionPdp?: ArrayFieldType<ContentContainerCompatibleContentType>;
  bottomSectionPdp?: ArrayFieldType<ContentContainerCompatibleContentType>;
  newCustomerBanner?: EntryFieldTypes.EntryLink<BannerContentType>;
  existingCustomerBanner?: EntryFieldTypes.EntryLink<BannerContentType>;
  sponsoredProducts?: ArrayFieldType<SponsoredContentType>;
}
export type ContentPageContentType = EntrySkeletonType<ContentPageFields, 'contentPage'>;
export type ContentPageEntryType = WithoutUnResolveable<ContentPageContentType>;

//Landing Page Content-Type
export interface LandingPageFields {
  entryTitle: EntryFieldTypes.Text;
  id?: EntryFieldTypes.Symbol;
  slug: EntryFieldTypes.Text;

  // Main Products
  productCodes: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customProductTitles: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;

  // Hero Section
  pageEyebrow: EntryFieldTypes.Text;
  pageTitle: EntryFieldTypes.Text;
  pageSubheadline: EntryFieldTypes.Text;
  heroVoucherText: EntryFieldTypes.Text;
  heroVoucherIcon: EntryFieldTypes.EntryLink<ImageContentType>;
  voucherBackgroundColor: EntryFieldTypes.Text;
  voucherTextColor: EntryFieldTypes.Text;
  heroBulletPoints: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  heroCustomCtaText: EntryFieldTypes.Text;
  heroImageDesktop: EntryFieldTypes.EntryLink<ImageContentType>;
  heroImageMobile: EntryFieldTypes.EntryLink<ImageContentType>;
  heroTextAfterPrice: EntryFieldTypes.Text;
  // Benefits Section
  benefitsColumnsHeadline: EntryFieldTypes.Text;
  benefitsItemTitles: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  benefitsItemBody: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  benefitsItemIcons: EntryFieldTypes.EntryLink<ImageContentType>;
  // Highlight Section
  highlightItemHeadlines: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  highlightItemBody: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  highlightItemImages: EntryFieldTypes.EntryLink<ImageContentType>;
  highlightBackgroundColor: EntryFieldTypes.Text;
  highlightTextColor: EntryFieldTypes.Text;
  // Content Banner Section
  contentBannerImage: EntryFieldTypes.EntryLink<ImageContentType>;
  contentBannerImagePlacement: EntryFieldTypes.Boolean;
  contentBannerHeadline: EntryFieldTypes.Text;
  contentBannerBody: EntryFieldTypes.Text;
  contentBannerBackgroundColor: EntryFieldTypes.Text;
  contentBannerTextColor: EntryFieldTypes.Text;
  // Custom Reviews Section
  customerReviewsHeadline: EntryFieldTypes.Text;
  customerIcons: EntryFieldTypes.EntryLink<ImageContentType>;
  customerReviewsNames: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customerReviewsTitles: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customerReviewsBodys: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customerReviewsDates: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customerReviewsStarRatings: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  customerReviewsLogoIcon: EntryFieldTypes.EntryLink<ImageContentType>;
  // Image Cards Section
  imageCardsSectionHeadline: EntryFieldTypes.Text;
  imageCardsSectionDescription: EntryFieldTypes.Text;
  imageCardEyebrow: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  imageCardHeadline: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  imageCardImage: EntryFieldTypes.EntryLink<ImageContentType>;
  imageCardCopy: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
}

export type LandingPageContentType = EntrySkeletonType<LandingPageFields, 'landingPage'>;
export type LandingPageEntryType = WithoutUnResolveable<LandingPageContentType>;

// Voucher landing page Content-type
export interface VoucherWidgetFields {
  entryTitle: EntryFieldTypes.Text;
  backgroundImage: EntryFieldTypes.EntryLink<ImageContentType>;
  promoText: EntryFieldTypes.Text;
  eyebrow: EntryFieldTypes.Text;
  headline: EntryFieldTypes.Text;
  description: EntryFieldTypes.Text;
  voucherCodesList: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  ctaText: EntryFieldTypes.Text;
  subCtaText: EntryFieldTypes.EntryLink<RichTextContentType>;
}

export type VoucherWidgetContentType = EntrySkeletonType<VoucherWidgetFields, 'voucherWidget'>;
export type VoucherWidgetEntryType = WithoutUnResolveable<VoucherWidgetContentType>;

//Conditional Container Content-Type
export interface ConditionalContainerFields {
  entryTitle?: EntryFieldTypes.Text;
  content: ArrayFieldType<ContentContainerCompatibleContentType>;
  //TODO: should conditions be more specific types?
  customerLoggedIn: EntryFieldTypes.Text;
  conditionSubscription: EntryFieldTypes.Text;
  conditionVineyardPartner: EntryFieldTypes.Text;
  conditionWineFund: EntryFieldTypes.Text;
  conditionUnlimited: EntryFieldTypes.Text;
  conditionBreakpoint: EntryFieldTypes.Text;
  conditionWineryDirect: EntryFieldTypes.Text;
  conditionState: EntryFieldTypes.Text;
}
export type ConditionalContainerContentType = EntrySkeletonType<ConditionalContainerFields, 'conditionalContainer'>;
export type ConditionalContainerEntryType = WithoutUnResolveable<ConditionalContainerContentType>;

//Carousel Content-Type
export interface CarouselFields {
  entryTitle: EntryFieldTypes.Text;
  slides: ArrayFieldType<ContentContainerCompatibleContentType>;
  slidesPerBlock: EntryFieldTypes.Integer;
  slidesToScroll: EntryFieldTypes.Integer;
}
export type CarouselContentType = EntrySkeletonType<CarouselFields, 'carousel'>;
export type CarouselEntryType = WithoutUnResolveable<CarouselContentType>;

export type TimerFields = {
  entryTitle: EntryFieldTypes.Text;
  headline: EntryFieldTypes.Text;
  endTime: EntryFieldTypes.Text;
  textColor: EntryFieldTypes.Text;
  dayRange?: EntryFieldTypes.Number;
};
export type TimerContentType = EntrySkeletonType<TimerFields, 'timer'>;
export type TimerEntryType = WithoutUnResolveable<TimerContentType>;
export interface TimerProps {
  contentItem: TimerEntryType;
}

export interface MultiCaseSelectorFields {
  eyebrow: EntryFieldTypes.Text;
  headline: EntryFieldTypes.Text;
  subheadline: EntryFieldTypes.Text;
  paragraph: EntryFieldTypes.EntryLink<RichTextContentType>;
  image?: EntryFieldTypes.EntryLink<ImageContentType>;
  productCodes: EntryFieldTypes.Array<EntryFieldTypes.Symbol>;
  upsellProductCode: EntryFieldTypes.Text;
}
export type MultiCaseSelectorContentType = EntrySkeletonType<MultiCaseSelectorFields, 'MultiCaseSelector'>;
export type MultiCaseSelectorEntryType = WithoutUnResolveable<MultiCaseSelectorContentType>;

//---------------------------------------------------------
//Other Types

export interface ContentfulApiInitConfig {
  space: string;
  accessToken: string;
  host: string;
  environment: string;
  contentfulOrchestration: string;
  isPreview: boolean;
}

export interface AlgoliaApiConfig {
  algoliaAppId: string;
  algoliaSearchKey: string;
  algoliaIndexName: string;
  algoliaQuerySuggestions: string;
  insightAPIKey: string;
}

export enum CmsContentTypeId {
  Text = 'text',
  ProductCard = 'productCard',
  Widget = 'widget',
  Link = 'link',
  Image = 'image',
  Video = 'video',
  RichText = 'richtext',
  Banner = 'banner',
  MenuBanner = 'menuBanner',
  Accordion = 'accordion',
  Container = 'container',
  Navigation = 'navigation',
  Article = 'article',
  Carousel = 'carousel',
  Columns = 'columns',
  Column = 'Column',
  ConditionalContainer = 'conditionalContainer',
  Expander = 'expander',
  Tabs = 'tabs',
  Timer = 'timer',
  MultiCaseSelector = 'multiCaseSelector',
  HtmlCode = 'htmlCode',
  VoucherWidget = 'voucherWidget',
}
